// ============================================================
// Texto del aviso de privacidad que ve el trabajador antes de responder
// el cuestionario de riesgo psicosocial. Puro: sin red, sin DOM, sin
// Intl (la conversión de hora se hace a mano — ver formatEcuadorDateTime).
//
// Cada afirmación de este texto DEBE seguir siendo cierta del sistema
// tal como está implementado — si algo aquí cambia (por ejemplo, se
// empieza a guardar la IP, o cambia PSICOSOCIAL_MIN_GROUP_SIZE), este
// archivo debe actualizarse en el mismo cambio.
//
// PENDIENTES antes de abrir a clientes (NO resueltos en este archivo):
//   - Validar la base legal citada (Acuerdo Ministerial MDT-2017-0082)
//     con un abogado — no se ha verificado que sea la cita correcta o
//     suficiente.
//   - Verificar qué conservan realmente las copias de seguridad
//     (backups) de Supabase, y durante cuánto tiempo.
//   - Decidir si la participación en el cuestionario es voluntaria u
//     obligatoria — este texto no lo afirma en ningún sentido.
//   - Exigir que toda empresa registre un domicilio del responsable
//     antes de abrir una evaluación (hoy company_address puede ser
//     null, y el texto lo cubre con "no registrado").
// ============================================================

import { PublicNotice } from './psicosocial-envio';
import { PROGRESS_TTL_HOURS } from './psicosocial-formulario';
import { PSICOSOCIAL_MIN_GROUP_SIZE } from './psicosocial-calculo';

export const NOTICE_VERSION = '2026-09-c';

// Resta 5 horas a mano (Ecuador continental, UTC-5, sin horario de
// verano) en vez de usar Intl, para no depender de la zona horaria del
// entorno donde corre el código (navegador o Deno).
export function formatEcuadorDateTime(iso: string): string {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) {
    throw new Error(`formatEcuadorDateTime: fecha inválida: "${iso}"`);
  }
  const ecuadorMs = ms - 5 * 3600000;
  const d = new Date(ecuadorMs);
  const day = String(d.getUTCDate()).padStart(2, '0');
  const month = String(d.getUTCMonth() + 1).padStart(2, '0');
  const year = d.getUTCFullYear();
  const hours = String(d.getUTCHours()).padStart(2, '0');
  const minutes = String(d.getUTCMinutes()).padStart(2, '0');
  return `${day}/${month}/${year} ${hours}:${minutes} (hora de Ecuador)`;
}

function retentionPhrase(days: number): string {
  return days === 1 ? '1 día' : `${days} días`;
}

export interface NoticeSection {
  heading: string;
  paragraphs: string[];
}

export function buildNoticeSections(notice: PublicNotice): NoticeSection[] {
  const address = notice.company_address !== null ? notice.company_address : 'no registrado; consulte con la empresa';
  const email = notice.contact_email !== null ? notice.contact_email : 'no registrado';
  const phone = notice.contact_phone !== null ? notice.contact_phone : 'no registrado';

  let retentionParagraph =
    `Sus respuestas individuales y el registro de los códigos de acceso se eliminan de la base de datos activa una vez que pasan ${retentionPhrase(notice.retention_days)} ` +
    `desde que la empresa cierra la evaluación (el borrado se ejecuta una vez al día). Después solo quedan resultados de grupos de al menos ${PSICOSOCIAL_MIN_GROUP_SIZE} ` +
    `personas, que se conservan mientras la empresa use StorSafe.`;
  if (notice.due_at !== null) {
    retentionParagraph += ` La evaluación recibe respuestas hasta el ${formatEcuadorDateTime(notice.due_at)}.`;
  }

  return [
    {
      heading: 'Antes de empezar',
      paragraphs: [
        `Este cuestionario forma parte del programa de prevención de riesgos psicosociales que ${notice.company_name} aplica a todo su personal. Evaluación: ${notice.title}.`,
      ],
    },
    {
      heading: 'Qué se le pregunta',
      paragraphs: [
        `Son ${notice.item_count} afirmaciones sobre su trabajo. No se le pide nombre, cédula, correo, edad, género ni cargo, y no hay campos de comentarios.`,
      ],
    },
    {
      heading: 'Quién verá sus respuestas',
      paragraphs: [
        `Ningún usuario de la aplicación puede ver respuestas individuales. La empresa solo ve resultados de grupos de al menos ${PSICOSOCIAL_MIN_GROUP_SIZE} personas, después de que se cierre la evaluación.`,
      ],
    },
    {
      heading: 'Qué se guarda de usted',
      paragraphs: [
        `Sus respuestas se guardan sin su nombre. Su código no se guarda tal cual: solo se guarda una huella irreversible que permite comprobar que es válido y que se use una sola vez, y el sistema no registra quién lo recibió. Con sus respuestas no se guarda su dirección de red ni su navegador. Para evitar abusos, la dirección de red desde la que se conecta se registra aparte, sin relación con sus respuestas, y se borra automáticamente en menos de media hora. Mientras contesta, el avance se guarda en este dispositivo hasta ${PROGRESS_TTL_HOURS} horas, o hasta que lo borre o envíe sus respuestas.`,
      ],
    },
    {
      heading: 'Para qué se usan y con qué base legal',
      paragraphs: [
        'Para identificar qué aspectos del trabajo conviene mejorar y preparar el plan de acción del programa. Base legal: la obligación del empleador de aplicar el programa de prevención de riesgos psicosociales (Acuerdo Ministerial MDT-2017-0082).',
      ],
    },
    {
      heading: 'Cuánto tiempo se conservan',
      paragraphs: [retentionParagraph],
    },
    {
      heading: 'Quién es el responsable',
      paragraphs: [
        `Responsable del tratamiento: ${notice.company_name}. Domicilio: ${address}. Correo de contacto: ${email}. Teléfono: ${phone}. Proveedor que trata los datos por cuenta de la empresa: StorSafe SAS, info@storsafe.app.`,
      ],
    },
    {
      heading: 'Sus derechos',
      paragraphs: [
        'Puede solicitar acceso, rectificación, eliminación u oposición a la empresa o a info@storsafe.app. Como el sistema no sabe quién respondió, puede ser imposible ubicar una respuesta individual. También puede presentar un reclamo ante la Autoridad de Protección de Datos Personales.',
      ],
    },
  ];
}
